/* Redo HSA/FSA cart badge — EliteCart drawer integration for ÜNI. v1
 * Rebuilt selector layer for EliteCart (2026-08-05, copy-lock refresh
 * 2026-08-11). The proven UpCart (AfterSell) build
 * (~/Desktop/neubrain_hsa_cart_badge/, Perk Energy origin, live on NEUBRAIN
 * since 2026-07-30) does NOT bind here — EliteCart is a different cart app
 * with a different DOM shape. Root discovery and the fetch/poll loop are
 * ported verbatim from that build; the selector layer and the badge-
 * placement pattern changed for EliteCart's DOM (this is per-line-item; the
 * UpCart build's v5/v6 is one cart-level badge under the checkout button —
 * a different Boss-approved pattern per merchant, not a regression from one
 * to the other). The badge COPY strings are NOT verbatim from the UpCart
 * build as of 2026-08-11 — see the copy-lock note below.
 *
 * COPY LOCK (Boss, 2026-08-10, factory-wide, applied here 2026-08-11):
 * shopper-facing eligibility text is never "with a letter" / any euphemism
 * implying conditional eligibility. The original 2026-08-05 copy for
 * eligible_with_lmn ("HSA/FSA eligible for qualified customers.") was exactly
 * that euphemism and has been flattened to "HSA eligible." — matching the
 * factory's established letter-tier standard
 * (feedback_badge_copy_hsa_eligible_factory_standard). The statutory
 * ("eligible") string was normalized from "HSA/FSA accepted." to
 * "HSA/FSA eligible." for the same reason this file's tier copy exists at
 * all: two adjacent badges that use different VERBS ("accepted" vs
 * "eligible") for the same underlying claim reads as an unintended
 * distinction. This mirrors the factory's own statutory-vs-letter-tier
 * pairing on info pages ("HSA/FSA eligible" statutory / "HSA eligible"
 * letter-tier) rather than inventing new wording.
 *
 * SELECTOR CONFIDENCE — read before editing this file:
 *   - #elitecart-root, li[data-line-key].ec-lineitem-container,
 *     .ec-cart-modal / .ec-cart-container / .ec-cart-inner: OBSERVED
 *     verbatim in EliteCart's own bundle (fetched public, unauthenticated,
 *     2026-08-05: cdn.shopify.com/extensions/.../elitecart-upsell-338/
 *     assets/App-*.js + App-*.css). The "ec-*" class names and the
 *     data-line-key attribute read as EliteCart's own deliberate, unstyled
 *     JS hooks — confirmed because none of them carry a CSS rule in the
 *     app's own stylesheet; all real layout runs through Tailwind-utility
 *     "tw-*" classes on the same elements instead. Do not select on "tw-*"
 *     classes — they are the part a future EliteCart release can rename
 *     freely without it counting as a breaking change on their side.
 *   - Per-line-item variant id: INFERRED, not observed directly. The
 *     rendered line item has no per-item data-variant-id attribute in the
 *     markup this script touches; instead each <li> carries
 *     data-line-key={cart_item.key}, which is Shopify's own Ajax Cart API
 *     line key (confirmed in the bundle: the same object also reads
 *     .variant_id / .product_id in JS, off the identical Shopify cart-item
 *     shape). This script reads the numeric text before the first ":" in
 *     that key as the variant id — the standard Shopify shape is
 *     "{variant_id}" with no properties or "{variant_id}:{properties_hash}"
 *     with them. NOT verified against a live rendered drawer (no cart
 *     session reachable from a public unauthenticated fetch with real
 *     items in it) — first thing to check in the runbook's live test.
 *   - EliteCart mounts inside an OPEN shadow root when the storefront
 *     preloads its shadowMount-*.js chunk. Üni's homepage HTML includes
 *     that modulepreload, so this script keeps the UpCart build's
 *     shadow-root/iframe traversal completely unmodified — do not strip it
 *     as unnecessary for this merchant; it is the reason the badge will
 *     render at all here.
 *   - Exact inner placement inside a line item (next to the title vs. the
 *     price) could NOT be confirmed — the bundle's per-item layout wrapper
 *     divs are unnamed Tailwind utility strings with no stable hook. The
 *     badge is appended as the line item's LAST CHILD rather than targeting
 *     an inner wrapper: lower visual precision (it reads as a new line
 *     below the row, not tucked beside the price), but it cannot collide
 *     with an inner class EliteCart renames on its next build.
 *
 * DATA SOURCE: unchanged from the UpCart build — theme section
 * sections/redo-hsa-cart-data.liquid, fetched via the Section Rendering API
 * (GET {root}?sections=redo-hsa-cart-data), same {variantId: eligibility}
 * JSON map, same Gale-metafield resolution hierarchy, cart-app-agnostic.
 *
 * INSTALL: see INSTALL_RUNBOOK.md in this folder. Do not install before
 * Echo confirms Üni's variants actually carry gale_payments metafields —
 * see the runbook's blocking note.
 */
(() => {
  if (window.__redoHsaCartBadgeActive) return; /* run-once guard */
  window.__redoHsaCartBadgeActive = true;

  const LINEITEM_SELECTOR = "li.ec-lineitem-container[data-line-key]";
  const CART_CONTAINER_SELECTOR =
    "#elitecart-root, .ec-cart-modal, .ec-cart-container, .ec-cart-inner";
  const BADGE_CLASS = "redo-hsa-lineitem-badge";
  const SECTION_ID = "redo-hsa-cart-data";
  const FETCH_COOLDOWN_MS = 4000;
  const POLL_MS = 2000;
  const CHECK_GLYPH = "✓";
  const CHECK_COLOR = "#2e7d32"; /* eyeball match — retune for Üni if desired */
  const COPY = {
    eligible: "HSA/FSA eligible.",
    eligible_with_lmn: "HSA eligible.",
  };

  let map = {};
  let fetchInFlight = null;
  let lastFetchAt = 0;
  let noProgressFetches = 0;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function fetchMap() {
    if (fetchInFlight) return fetchInFlight;
    if (noProgressFetches >= 3) return Promise.resolve(map);
    const now = Date.now();
    if (now - lastFetchAt < FETCH_COOLDOWN_MS) return Promise.resolve(map);
    lastFetchAt = now;
    fetchInFlight = fetch(rootUrl() + "?sections=" + SECTION_ID)
      .then((r) => r.json())
      .then((json) => {
        const html = json && json[SECTION_ID];
        if (!html) {
          noProgressFetches += 1;
          return map;
        }
        const doc = new DOMParser().parseFromString(html, "text/html");
        const el = doc.querySelector("[data-redo-hsa-cart-map]");
        if (!el) {
          noProgressFetches += 1;
          return map;
        }
        const next = JSON.parse(el.textContent);
        const changed = JSON.stringify(next) !== JSON.stringify(map);
        map = next;
        noProgressFetches = changed ? 0 : noProgressFetches + 1;
        return map;
      })
      .catch((err) => {
        console.error("Redo HSA cart badge: map fetch failed", err);
        noProgressFetches += 1;
        return map;
      })
      .finally(() => {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  /* ---- root discovery across isolation boundaries ----
   * Ported verbatim from the UpCart build. EliteCart mounts into an open
   * shadow root on this merchant (see header note), so this is load-bearing
   * here, not defensive boilerplate. */

  let cartRoots = []; /* cached Document / ShadowRoot nodes containing EliteCart DOM */
  const observedRoots = new WeakSet();

  /* Recursively collect the top document, every open shadow root, and every
     same-origin iframe document (cross-origin iframes throw and are skipped). */
  function collectRoots(root, acc) {
    acc.push(root);
    let all;
    try {
      all = root.querySelectorAll("*");
    } catch (e) {
      return;
    }
    for (const el of all) {
      if (el.shadowRoot) collectRoots(el.shadowRoot, acc);
      if (el.tagName === "IFRAME") {
        try {
          if (el.contentDocument) collectRoots(el.contentDocument, acc);
        } catch (e) {
          /* cross-origin iframe — unreachable, skip */
        }
      }
    }
  }

  function rootIsAlive(r) {
    if (r === document) return true;
    if (r.nodeType === 9) {
      try {
        const frame = r.defaultView && r.defaultView.frameElement;
        return !!(frame && frame.isConnected);
      } catch (e) {
        return false;
      }
    }
    return !!(r.host && r.host.isConnected);
  }

  function observeRoot(r) {
    if (observedRoots.has(r)) return;
    try {
      const target = r.nodeType === 9 ? r.documentElement || r : r;
      new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
      observedRoots.add(r);
    } catch (e) {
      /* leave unobserved; the safety poll still covers this root */
    }
  }

  function ensureRoots() {
    cartRoots = cartRoots.filter(rootIsAlive);
    if (cartRoots.length === 0) {
      const acc = [];
      collectRoots(document, acc);
      cartRoots = acc.filter((r) => {
        try {
          return r.querySelector(LINEITEM_SELECTOR) || r.querySelector(CART_CONTAINER_SELECTOR);
        } catch (e) {
          return false;
        }
      });
      cartRoots.forEach(observeRoot);
    }
    return cartRoots;
  }

  /* ---- badge rendering ---- */

  function variantIdFor(tile) {
    const key = tile.getAttribute("data-line-key");
    if (!key) return null;
    const id = key.split(":")[0];
    return id || null;
  }

  function makeBadge(copyKey, ownerDoc) {
    const doc = ownerDoc || document;
    const badge = doc.createElement("div");
    badge.className = BADGE_CLASS;
    badge.setAttribute("data-redo-hsa-eligibility", copyKey);
    badge.style.cssText =
      "display:flex;align-items:flex-start;gap:5px;" +
      "margin:6px 0 0;font-family:'Inter',Arial,sans-serif;font-size:12px;" +
      "font-weight:500;line-height:1.4;color:#000;";

    const icon = doc.createElement("span");
    icon.textContent = CHECK_GLYPH;
    icon.setAttribute("aria-hidden", "true");
    icon.style.cssText =
      "color:" + CHECK_COLOR + ";font-weight:700;font-size:13px;" +
      "line-height:inherit;flex-shrink:0;";

    const text = doc.createElement("span");
    text.textContent = COPY[copyKey];

    badge.appendChild(icon);
    badge.appendChild(text);
    return badge;
  }

  /* Idempotent: safe to run on every DOM change; settles with no mutations.
     Per line item, not per cart — each tile gets its own badge or none. */
  function apply() {
    if (window.Shopify && window.Shopify.country && window.Shopify.country !== "US") {
      return;
    }
    let sawUnknownVariant = false;
    for (const root of ensureRoots()) {
      let tiles;
      try {
        tiles = root.querySelectorAll(LINEITEM_SELECTOR);
      } catch (e) {
        continue;
      }

      tiles.forEach((tile) => {
        try {
          const id = variantIdFor(tile);
          const existing = tile.querySelector("." + BADGE_CLASS);

          if (!id) {
            if (existing) existing.remove();
            return;
          }

          const eligibility = map[id];

          if (eligibility === undefined) {
            /* map hasn't caught up to this line yet — leave any existing
               badge alone and ask for a fresh map, don't flap it off. */
            sawUnknownVariant = true;
            return;
          }

          if (eligibility !== "eligible" && eligibility !== "eligible_with_lmn") {
            if (existing) existing.remove();
            return;
          }

          if (!existing) {
            tile.appendChild(makeBadge(eligibility, tile.ownerDocument));
          } else if (existing.getAttribute("data-redo-hsa-eligibility") !== eligibility) {
            const replacement = makeBadge(eligibility, tile.ownerDocument);
            existing.replaceWith(replacement);
          }
        } catch (e) {
          /* one malformed tile never takes the rest of the cart down */
        }
      });
    }
    if (sawUnknownVariant) {
      fetchMap().then(schedule);
    }
  }

  /* Coalesce observer bursts into one apply() per frame; apply() is
     idempotent so self-triggered mutations settle immediately. */
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      apply();
    });
  }

  function init() {
    observeRoot(document);
    setInterval(schedule, POLL_MS);
    fetchMap().then(schedule);
  }

  /* Debugging helpers. */
  window.redoHsaCartBadgeRefresh = function () {
    lastFetchAt = 0;
    noProgressFetches = 0;
    cartRoots = [];
    fetchMap().then(schedule);
  };
  window.redoHsaCartBadgeDebug = function () {
    const roots = ensureRoots();
    return {
      map: map,
      roots: roots.map((r) =>
        r === document
          ? "top-document"
          : r.nodeType === 9
          ? "iframe-document"
          : "shadow-root(" + (r.host && r.host.tagName) + "#" + (r.host && r.host.id) + ")"
      ),
      lineItems: roots.reduce((n, r) => n + r.querySelectorAll(LINEITEM_SELECTOR).length, 0),
      badges: roots.reduce((n, r) => n + r.querySelectorAll("." + BADGE_CLASS).length, 0),
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
